import { IntegrationType } from "pages/subscription-settings/integrations-tab-new/utils";
import { getAccessToken } from "./auth";
import CIQClient from "./ciq-client";
import { INTEGRATION_BASE_URL } from "./endpoints";

export const getOAuthUrl = async (
  system: string,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/oauth/new-auth/init`,
    data: payload,
    config
  });
};

export const getCompanies = async (
  system: string,
  authId: string,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  const endpoint =
    system === IntegrationType[IntegrationType.AUTODESK]
      ? "accounts"
      : "companies";

  return CIQClient.get({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/oauth_operation/${authId}/${endpoint}`,
    config
  });
};

export const createSubscriptionIntegration = async (
  system: string,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/subscription_config`,
    data: payload,
    config
  });
};

export const updateSubscriptionIntegration = async (
  system: string,
  integrationConfigId: any,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.put({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/subscription_config/${integrationConfigId}`,
    data: payload,
    config
  });
};

export const getCompanyAccounts = (
  system: string,
  payload: { subscriptionConfigId: string },
  projectToken?: any
) => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.get({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/subscription_config/${
      payload.subscriptionConfigId
    }/projects`,
    config
  });
};

export const getAccountProjects = (
  system: string,
  payload: { accountId: string; oauthOperationId: string },
  projectToken?: any
) => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.get({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/oauth_operation/${
      payload.oauthOperationId
    }/account/${payload.accountId}/projects`,
    config
  });
};

export const createProjectIntegration = async (
  system: string,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/project_config`,
    data: payload,
    config
  });
};
export const updateProjectIntegration = async (
  system: string,
  integrationConfigId: any,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.put({
    url: `${INTEGRATION_BASE_URL}/${system.toLowerCase()}/project_config/${integrationConfigId}`,
    data: payload,
    config
  });
};

// Despite the name this endpoint is system-agnostic — it takes whichever
// project_integration_configs row it is handed, which is why the ACC submittal
// sync, the file-based import and the OPC schedule sync all go through it.
export const invokeACCProjectSyncJob = (
  data: {
    project_config_id: string;
    attachment_id?: string;
    // Asks the backend to run the job now rather than deferring to the
    // integration's own sync schedule.
    force_sync?: boolean;
  },
  projectToken: string,
  // Left on by default so the submittal log's sync button keeps the generic
  // ErrorManager toast it has always shown. Callers that report the failure
  // themselves pass false, otherwise the user gets two toasts for one failure —
  // ErrorManager never displays the server's own message, only a generic one.
  showError = true
): Promise<any> => {
  const config = {
    headers: { Authorization: `Bearer ${projectToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/invoke-project-sync-job`,
    data,
    config,
    showError
  });
};

// OPC is Oracle Primavera Cloud, so what it hands back is a P6 schedule. The
// same value the file-upload import sends for a .xer.
export const OPC_EXPORT_FILE_TYPE = "P6";

/**
 * Asks OPC to export one schedule so CIQ can import it. Named after the
 * endpoint — OPC calls it an export, CIQ calls the result an import, and every
 * user-facing string in this feature says "import".
 *
 * Returns immediately with the `import_log_id` of the job it started; the row's
 * icon is driven by polling that id, not by this response.
 *
 * `opc` is a path literal rather than a `system` parameter like its neighbours
 * above, because this route exists only for OPC.
 */
export const exportOpcScheduleSourceInfo = (
  scheduleSourceInfoId: string,
  data: {
    schedule_name: string;
    // The schedule's Date Added, as YYYY-MM-DD — the shape
    // schedule_import_details.schedule_date is stored in, and what the project
    // schedule's own date editor writes. Optional so a row whose OPC metadata
    // carried no date sends no key at all rather than an empty string, which a
    // date column has nothing to do with.
    schedule_date?: string;
    file_type: string;
  },
  projectToken: string,
  // As on invokeACCProjectSyncJob above: left on by default, and switched off by
  // a caller that reports the failure itself, otherwise the user gets two toasts
  // for one failure — ErrorManager never displays the server's own message.
  showError = true
): Promise<any> => {
  const config = {
    headers: { Authorization: `Bearer ${projectToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/opc/schedule_source_info/${scheduleSourceInfoId}/export`,
    data,
    config,
    showError
  });
};

export const saveACCIntegrationConfig = (
  properties: {
    integration_name: string;
    autodesk_project_name?: string;
    project_alert_email?: string;
  },
  projectToken: string,
  integrationMethod: string,
  integrationSourceType: string
): Promise<any> => {
  const config = {
    headers: { Authorization: `Bearer ${projectToken}` }
  };
  const payload = [
    {
      properties
    }
  ];

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/source_type/${integrationSourceType}/method_type/${integrationMethod}/project_config`,
    data: payload,
    config
  });
};

export const authorizeOpcIntegration = async (payload: {
  username: string;
  password: string;
  endpoint_url: string;
}): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/opc/basic-auth/init`,
    data: payload,
    config
  });
};

export const getOpcWorkspaces = async (
  opcAuthOperationId: string,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.get({
    url: `${INTEGRATION_BASE_URL}/opc/auth_operation/${opcAuthOperationId}/workspaces`,
    config
  });
};

export const getOpcWorkspaceProjects = async (
  opcWorkspaceId: string,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.get({
    url: `${INTEGRATION_BASE_URL}/opc/workspace/${opcWorkspaceId}/projects`,
    config
  });
};

export const createOpcSubscriptionConfig = async (
  payload: Array<{
    opc_auth_operation_id: string;
    properties: {
      integration_name: string;
      opc_workspace_id: string;
      opc_workspace_name: string;
      opc_workspace_code: string;
      [key: string]: string;
    };
  }>,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/source_type/opc/method_type/file/subscription_config`,
    data: payload,
    config
  });
};

export const updateOpcSubscriptionConfig = async (
  subscriptionConfigId: string,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.put({
    url: `${INTEGRATION_BASE_URL}/source_type/opc/method_type/file/subscription_config/${subscriptionConfigId}`,
    data: payload,
    config
  });
};

export const createOpcProjectConfig = async (
  payload: Array<{
    subscription_config_id: string;
    opc_auth_operation_id?: string;
    properties: {
      integration_name: string;
      opc_workspace_id: string;
      opc_workspace_name: string;
      opc_workspace_code: string;
      opc_project_id: string;
      opc_project_name: string;
      [key: string]: string;
    };
  }>,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.post({
    url: `${INTEGRATION_BASE_URL}/source_type/opc/method_type/file/project_config`,
    data: payload,
    config
  });
};

export const updateOpcProjectConfig = async (
  projectConfigId: string,
  payload: any,
  projectToken?: string
): Promise<any> => {
  const subscriptionToken: any = getAccessToken();
  const config = {
    headers: { Authorization: `Bearer ${projectToken || subscriptionToken}` }
  };

  return CIQClient.put({
    url: `${INTEGRATION_BASE_URL}/source_type/opc/method_type/file/project_config/${projectConfigId}`,
    data: payload,
    config
  });
};
